import React, { FunctionComponent } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Round, Text, baseColors } from '@fairhq/common'

const CompletionTag = styled.div<{
  backgroundColor: string
  color: string
  borderColor: string
}>`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 16px 4px 12px;
  border-radius: 9999px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ color }) => color};
  white-space: nowrap;
  border: 1px solid ${({ borderColor }) => borderColor};
`

const StyledRound = styled(Round)`
  flex-shrink: 0;
`

interface StatusTagWithRoundIconProps {
  percentage: number
  withCompleteLabels?: boolean
}

export const StatusTagWithRoundIcon: FunctionComponent<
  StatusTagWithRoundIconProps
> = ({ percentage, withCompleteLabels = true }) => {
  const { t } = useTranslation()

  if (percentage === 0) {
    return (
      <CompletionTag
        backgroundColor={baseColors.backgroundsBackground}
        color={baseColors.contentMuted}
        borderColor={baseColors.borderDefault}
      >
        <Text type="body" element="p" size="xs" fontWeight={500}>
          {t('reports.notStarted')}
        </Text>
      </CompletionTag>
    )
  }

  if (percentage > 0 && percentage < 100) {
    return (
      <CompletionTag
        backgroundColor={baseColors.backgroundsPrimarySurface}
        color={baseColors.contentContent}
        borderColor={baseColors.borderPrimary}
      >
        <StyledRound
          width="14px"
          height="14px"
          outline={baseColors.backgroundsPrimarySurface}
          stroke={baseColors.borderPrimary}
        />
        <Text type="body" element="p" size="xs" fontWeight={500}>
          {withCompleteLabels
            ? t('reports.complete', { percent: Math.round(percentage) })
            : `${Math.round(percentage)}%`}
        </Text>
      </CompletionTag>
    )
  }

  if (percentage === 100) {
    return (
      <CompletionTag
        backgroundColor={baseColors.backgroundSuccessSurface}
        color={baseColors.contentSuccess}
        borderColor={baseColors.borderSuccess}
      >
        <StyledRound
          width="14px"
          height="14px"
          outline={baseColors.backgroundSuccessSurface}
          stroke={baseColors.borderSuccess}
        />
        <Text type="body" element="p" size="xs" fontWeight={500}>
          {withCompleteLabels ? t('reports.completed') : t(`reports.ready`)}
        </Text>
      </CompletionTag>
    )
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <></>
}
